import { CommonModule } from '@angular/common';
import { Component, DestroyRef, ElementRef, Injector, afterNextRender, computed, effect, inject, input, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { catchError, firstValueFrom, map, of, switchMap } from 'rxjs';

import { SessionStore } from '../../../core/auth/session.store';
import { TranslationService } from '../../../core/i18n/translation.service';
import { AuditsApi } from '../../audits/services/audits.api';
import { UserAdminApi } from '../../users/services/user-admin.api';
import { IssueDto } from '../../issues/models/issue.models';
import { DashboardApi } from '../services/dashboard.api';
import { AuditFilter, DashboardAudit, DashboardIssue, DashboardLocationAudit, DashboardPage, DashboardSummary, IssueFilter } from '../models/dashboard.models';
import { DashboardStatusChartComponent } from './dashboard-status-chart.component';
import { DashboardLocationAuditsComponent } from './dashboard-location-audits.component';

@Component({
  selector: 'app-dashboard',
  imports: [CommonModule, DashboardStatusChartComponent, DashboardLocationAuditsComponent],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css',
})
export class DashboardComponent {
  protected readonly i18n = inject(TranslationService);
  private readonly api = inject(DashboardApi);
  private readonly auditsApi = inject(AuditsApi);
  private readonly users = inject(UserAdminApi);
  private readonly session = inject(SessionStore);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly listSection = viewChild<ElementRef<HTMLElement>>('listSection');
  readonly createAudit = output<void>();
  readonly editIssue = output<IssueDto>();
  readonly reloadRequest = input(0);
  protected readonly data = signal<DashboardSummary | null>(null);
  protected readonly locations = signal<Array<{ id: string; name: string }>>([]);
  protected readonly loading = signal(true);
  protected readonly error = signal('');
  protected readonly period = signal(30);
  protected readonly location = signal('');
  protected readonly mine = signal(false);
  protected readonly selectedAudit = signal<DashboardAudit | null>(null);
  protected readonly selectedIssue = signal<DashboardIssue | null>(null);
  protected readonly pendingPlan = signal<string | null>(null);
  protected readonly actionError = signal('');
  protected readonly explorer = signal<{ kind: 'audits'; filter: AuditFilter } | { kind: 'issues'; filter: IssueFilter } | null>(null);
  protected readonly auditPage = signal<DashboardPage<DashboardAudit> | null>(null);
  protected readonly issuePage = signal<DashboardPage<DashboardIssue> | null>(null);
  protected readonly listLoading = signal(false);
  protected readonly listError = signal('');
  protected readonly currentPage = signal(1);
  protected readonly auditDate = signal('');
  private requestId = 0;
  private listRequestId = 0;
  private readonly annualRequestKeys = new Map<string, string>();
  private readonly userCode = computed(() => this.session.session()?.userCode);
  private readonly dashboardContext = computed(() => {
    const session = this.session.session();
    return `${session?.userCode}|${session?.schema}|${session?.role}`;
  });
  protected readonly firstName = toSignal(toObservable(this.userCode).pipe(switchMap(code =>
    code ? this.users.getUser(code).pipe(map(user => user.first_name.trim()), catchError(() => of(''))) : of(''),
  )), { initialValue: '' });
  protected readonly isAdmin = computed(() => this.session.session()?.role === 'BUSINESS_ADMIN');
  protected readonly showAuditRole = computed(() => ['BUSINESS_ADMIN', 'MANAGER'].includes(this.session.session()?.role ?? ''));
  protected readonly auditCount = computed(() => this.data()?.audit_statuses.reduce((n, row) => n + row.count, 0) ?? 0);
  protected readonly issueCount = computed(() => this.data()?.issue_statuses.reduce((n, row) => n + row.count, 0) ?? 0);
  protected readonly pages = computed(() => Math.max(1, Math.ceil((this.explorer()?.kind === 'audits' ? this.auditPage()?.count ?? 0 : this.issuePage()?.count ?? 0) / 10)));
  protected readonly issueKpis = [
    { key: 'issues_rejected' as const, kind: 'issues' as const, filter: 'rejected' as const, icon: 'fa-circle-xmark', tone: 'warning' },
    { key: 'issues_new' as const, kind: 'issues' as const, filter: 'new' as const, icon: 'fa-shield-halved', tone: 'info' },
    { key: 'issues_in_progress' as const, kind: 'issues' as const, filter: 'in_progress' as const, icon: 'fa-clock-rotate-left', tone: 'teal' },
  ];
  protected readonly auditKpis = [
    { key: 'audits_todo' as const, kind: 'audits' as const, filter: 'todo' as const, icon: 'fa-clipboard-list', tone: 'info' },
    { key: 'audits_in_progress' as const, kind: 'audits' as const, filter: 'in_progress' as const, icon: 'fa-arrows-rotate', tone: 'teal' },
    { key: 'audits_overdue' as const, kind: 'audits' as const, filter: 'overdue' as const, icon: 'fa-triangle-exclamation', tone: 'danger' },
  ];

  constructor() {
    this.destroyRef.onDestroy(() => { ++this.requestId; ++this.listRequestId; });
    effect(() => {
      this.period(); this.location(); this.mine(); this.reloadRequest(); this.dashboardContext();
      void this.load();
    });
  }

  protected async load(): Promise<void> {
    const request = ++this.requestId;
    ++this.listRequestId;
    this.data.set(null); this.error.set(''); this.loading.set(true);
    this.explorer.set(null); this.selectedAudit.set(null); this.selectedIssue.set(null);
    this.actionError.set('');
    const end = new Date();
    const start = new Date(end.getFullYear(), end.getMonth(), end.getDate() - this.period() + 1);
    try {
      const data = await firstValueFrom(this.api.summary(this.isoDate(start), this.isoDate(end), this.location(), this.mine()).pipe(takeUntilDestroyed(this.destroyRef)));
      if (request !== this.requestId) return;
      this.data.set(data); this.locations.set(data.locations);
    } catch {
      if (request === this.requestId) this.error.set(this.i18n.t('dashboard.overview.error'));
    } finally {
      if (request === this.requestId) this.loading.set(false);
    }
  }

  protected openAudits(filter: AuditFilter): void {
    this.auditDate.set(''); this.selectedAudit.set(null);
    this.explorer.set({ kind: 'audits', filter }); this.currentPage.set(1); void this.loadList();
    this.scrollToList();
  }
  protected setAuditDate(value: string): void {
    if (this.explorer()?.kind !== 'audits' || value === this.auditDate()) return;
    this.auditDate.set(value); this.currentPage.set(1); this.selectedAudit.set(null);
    void this.loadList();
  }
  protected selectToday(): void { this.setAuditDate(this.isoDate(new Date())); }
  protected openIssues(filter: IssueFilter): void {
    this.explorer.set({ kind: 'issues', filter }); this.currentPage.set(1); void this.loadList();
    this.scrollToList();
  }
  private scrollToList(): void {
    const selection = this.explorer();
    afterNextRender(() => {
      if (this.explorer() !== selection) return;
      const section = this.listSection()?.nativeElement;
      if (!section) return;
      const topbar = section.closest('.main-page')?.querySelector('.main-page__topbar');
      // Reserve enough space below the target even while loading or showing a short list.
      section.style.setProperty('--dashboard-list-offset', `${(topbar?.getBoundingClientRect().height ?? 0) + 8}px`);
      const reducedMotion = section.ownerDocument.defaultView?.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      section.scrollIntoView({ behavior: reducedMotion ? 'instant' : 'smooth', block: 'start' });
    }, { injector: this.injector });
  }
  protected openKpi(item: typeof this.issueKpis[number] | typeof this.auditKpis[number]): void {
    if (item.kind === 'audits') this.openAudits(item.filter); else this.openIssues(item.filter);
  }
  protected closeList(): void { ++this.listRequestId; this.explorer.set(null); }
  protected turnPage(delta: number): void {
    this.currentPage.update(page => Math.min(this.pages(), Math.max(1, page + delta)));
    void this.loadList();
  }
  protected async loadList(): Promise<void> {
    const selection = this.explorer();
    if (!selection) return;
    const request = ++this.listRequestId;
    this.listLoading.set(true); this.listError.set(''); this.auditPage.set(null); this.issuePage.set(null);
    try {
      if (selection.kind === 'audits') {
        const page = await firstValueFrom(this.api.audits(selection.filter, this.currentPage(), this.location(), this.mine(), this.auditDate()).pipe(takeUntilDestroyed(this.destroyRef)));
        if (request === this.listRequestId) this.auditPage.set(page);
      } else {
        const page = await firstValueFrom(this.api.issues(selection.filter, this.currentPage(), this.location(), this.mine()).pipe(takeUntilDestroyed(this.destroyRef)));
        if (request === this.listRequestId) this.issuePage.set(page);
      }
    } catch {
      if (request === this.listRequestId) this.listError.set(this.i18n.t('dashboard.overview.error'));
    } finally {
      if (request === this.listRequestId) this.listLoading.set(false);
    }
  }

  protected async openAudit(row: DashboardAudit): Promise<void> {
    if (this.pendingPlan()) return;
    this.actionError.set('');
    if (row.action === 'details') { this.selectedAudit.set(row); return; }
    await this.executePlan(row.plan_id, row.action);
  }
  protected async openLocationAudit(row: DashboardLocationAudit): Promise<void> {
    if (!row.action || this.pendingPlan()) return;
    if (row.action === 'start_annual') {
      const key = `${this.dashboardContext()}|${row.audit_id}|${row.location_id}|${row.role}`;
      const requestId = this.annualRequestKeys.get(key) ?? crypto.randomUUID();
      this.annualRequestKeys.set(key, requestId);
      const context = this.dashboardContext();
      this.pendingPlan.set(row.id); this.actionError.set('');
      try {
        const plan = await firstValueFrom(this.auditsApi.prepareAnnualExecution(row.audit_id, row.location_id, requestId).pipe(takeUntilDestroyed(this.destroyRef)));
        if (this.destroyRef.destroyed || this.dashboardContext() !== context) return;
        if (await this.executePlan(plan.id, plan.status === 'completed' ? 'preview' : 'start')) this.annualRequestKeys.delete(key);
      } catch {
        if (!this.destroyRef.destroyed && this.dashboardContext() === context) this.actionError.set(this.i18n.t('dashboard.overview.actionError'));
      } finally { this.pendingPlan.set(null); }
      return;
    }
    if (!row.plan_id) return;
    await this.executePlan(row.plan_id, row.action);
  }
  protected async previewExecution(planId: string): Promise<void> {
    await this.executePlan(planId, 'preview');
  }
  private async executePlan(planId: string, action: 'start' | 'continue' | 'preview'): Promise<boolean> {
    const context = this.dashboardContext();
    this.actionError.set('');
    this.pendingPlan.set(planId);
    try {
      if (action === 'start' || action === 'continue') await firstValueFrom(this.auditsApi.startAuditExecutionPlan(planId).pipe(takeUntilDestroyed(this.destroyRef)));
      if (this.destroyRef.destroyed || this.dashboardContext() !== context) return false;
      return await this.router.navigate(['/app/audit-executions', planId]);
    } catch {
      if (!this.destroyRef.destroyed && this.dashboardContext() === context) this.actionError.set(this.i18n.t('dashboard.overview.actionError'));
      return false;
    }
    finally { this.pendingPlan.set(null); }
  }
  protected showIssueDetails(row: DashboardIssue): void {
    this.selectedIssue.set(this.selectedIssue()?.issue.id === row.issue.id ? null : row);
  }
  protected fixIssue(row: DashboardIssue): void {
    if (row.can_edit) {
      this.editIssue.emit(row.issue);
    }
  }
  protected date(value: string | null): string {
    if (!value) return '—';
    const date = value.length === 10 ? new Date(`${value}T12:00:00`) : new Date(value);
    return new Intl.DateTimeFormat(this.i18n.language() === 'pl' ? 'pl-PL' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
  }
  protected badge(status: string): string {
    const mapping: Record<string, string> = { completed: 'active', resolved: 'active', in_progress: 'in-progress', overdue: 'overdue', missed: 'overdue', planned: 'scheduled', new: 'draft', rejected: 'rejected', cancelled: 'archived', archived: 'archived', todo: 'draft' };
    return `app-status-chip app-status-chip--${mapping[status] ?? 'archived'}`;
  }
  protected auditTone(status: string): string {
    const tones: Record<string, string> = { todo: 'warning', planned: 'info', in_progress: 'info', overdue: 'danger', missed: 'danger', completed: 'success' };
    return tones[status] ?? 'neutral';
  }
  protected statusIcon(status: string): string {
    const icons: Record<string, string> = {
      completed: 'fa-circle-check', resolved: 'fa-circle-check', in_progress: 'fa-clock-rotate-left',
      overdue: 'fa-calendar-xmark', missed: 'fa-triangle-exclamation', planned: 'fa-calendar-day',
      todo: 'fa-clipboard-list', new: 'fa-circle-info', rejected: 'fa-circle-xmark',
      cancelled: 'fa-circle-xmark', archived: 'fa-box-archive',
    };
    return `fa-solid ${icons[status] ?? 'fa-circle-info'}`;
  }
  private isoDate(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }
}
